import { Component, Knobs, Accents, Story, Icons } from './DocTypes';

const PlaygroundStoryKnobs: Knobs = {
  text: { default: 'Click me', editor: 'textbox' },
  accent: {
    default: '',
    editor: 'dropdown',
    editorOptions: [''].concat(Accents),
  },
  icon: {
    default: '',
    editor: 'dropdown',
    editorOptions: [''].concat(Icons),
  },
  iconAccent: {
    default: '',
    editor: 'dropdown',
    editorOptions: [''].concat(Accents),
  },
  size: {
    default: '',
    editor: 'dropdown',
    editorOptions: ['', 'default', 'small', 'large'],
  },
  badge: { default: '', editor: 'textbox' },
  badgeAccent: { default: '', editor: 'dropdown', editorOptions: [''].concat(Accents) },
  badgeInline: { default: false, editor: 'checkbox' },
  rounded: {
    default: false,
    editor: 'checkbox',
  },
  squared: {
    default: false,
    editor: 'checkbox',
  },
  disabled: {
    default: false,
    editor: 'checkbox',
  },
  'colour-scheme': {
    editor: 'dropdown',
    editorOptions: ['light', 'dark', 'default', ''],
    default: '',
  },
  device: {
    editor: 'dropdown',
    editorOptions: ['mobile', 'desktop', ''],
    default: '',
  },
};
const playgroundStory: Story = {
  title: 'Playground',
  description: 'A standard button with various attributes',
  coreComponent: {
    html: `<tag-button{{...knobs}}>
</tag-button>`,
    knobs: PlaygroundStoryKnobs,
  },
  coreComponent_v3: {
    html: `<tag3-button{{...knobs}}></tag3-button>`,
    knobs: PlaygroundStoryKnobs,
  },
  reactWrapper: { jsx: `<TagButton{{...knobs}} />`, knobs: PlaygroundStoryKnobs },
  reactWrapper_v3: { jsx: `<Tag3Button{{...knobs}} id="1" />`, knobs: PlaygroundStoryKnobs },
};

const menuStory: Story = {
  title: 'Menu',
  description: 'A button menu where clicking the button opens the menu',
  coreComponent: {
    html: `<tag-button text='menu'>
  <tag-menu-item name='approve' icon='Tick' text='Approve' description='Use this option to approve'></tag-menu-item>
  <tag-menu-item name='cancel' icon='Cross' text='Cancel' description='Use this option to cancel'></tag-menu-item>
</tag-button>`,
  },
  coreComponent_v3: {
    html: `<tag3-button text='menu'>
      <tag3-menu-item name='approve' icon='Tick' text='Approve' description='Use this option to approve'></tag3-menu-item>
      <tag3-menu-item name='cancel' icon='Cross' text='Cancel' description='Use this option to cancel'></tag3-menu-item>
    </tag3-button>`,
  },
  reactWrapper: {
    jsx: `<TagButton
  text='menu'
  menuItems={[{
    name: 'approve',
    icon: 'Tick',
    text: 'Approve',
    description: 'Use this option to approve'
  }, {
    name: 'cancel',
    icon: 'Cross',
    text: 'Cancel',
    description: 'Use this option to cancel'
  }]}
/>`,
  },
  reactWrapper_v3: {
    jsx: `<Tag3Button
  text='menu'
  menuItems={[{
    name: 'approve',
    icon: 'Tick',
    text: 'Approve',
    description: 'Use this option to approve'
  }, {
    name: 'cancel',
    icon: 'Cross',
    text: 'Cancel',
    description: 'Use this option to cancel'
  }]}
/>`,
  },
};

const subMenuStory: Story = {
  title: 'Menu with submenu',
  description: 'A button menu that has a sub menu',
  coreComponent: {
    html: `<tag-button text='menu'>
  <tag-menu-item name='approve' icon='Tick' text='Approve' is-submenu>
    <tag-menu-item name='approveThis' text='This item'></tag-menu-item>
    <tag-menu-item name='approveAll' text='All items'></tag-menu-item>
  </tag-menu-item>
  <tag-menu-item name='cancel' icon='Cross' text='Cancel' description='Use this option to cancel'></tag-menu-item>
</tag-button>`,
  },
  coreComponent_v3: {
    html: `<tag3-button text='menu'>
  <tag3-menu-item name='approve' icon='Tick' text='Approve' is-submenu>
    <tag3-menu-item name='approveThis' text='This item'></tag3-menu-item>
    <tag3-menu-item name='approveAll' text='All items'></tag3-menu-item>
  </tag3-menu-item>
  <tag3-menu-item name='cancel' icon='Cross' text='Cancel' description='Use this option to cancel'></tag3-menu-item>
</tag3-button>`,
  },
  reactWrapper: {
    jsx: `<TagButton
  text='menu'
  menuItems={[{
      name: 'approve',
      icon: 'Tick',
      text: 'Approve',
      description: 'Use this option to approve',
      isSubmenu: 'true',
      nestedItems: [
        {
          name: 'approveThis',
          text: 'This item',
        },
        {
          name: 'approveAll',
          text: 'All items',
        }
      ],
    },
    {
      name: 'cancel',
      icon: 'Cross',
      text: 'Cancel',
      description: 'Use this option to cancel',
    }
  ]}
/>`,
  },
  reactWrapper_v3: {
    jsx: `
    <Tag3Button
      text='menu'
      menuItems={[{
          name: 'approve',
          icon: 'Tick',
          text: 'Approve',
          description: 'Use this option to approve',
          isSubmenu: 'true',
          nestedItems: [
            {
              name: 'approveThis',
              text: 'This item',
            },
            {
              name: 'approveAll',
              text: 'All items',
            }
          ],
        },
        {
          name: 'cancel',
          icon: 'Cross',
          text: 'Cancel',
          description: 'Use this option to cancel',
        }
      ]}
    />`,
  },
};

const segmentedMenuStory: Story = {
  title: 'Segmented menu',
  description: 'A button that can do an action as well as having a menu',
  coreComponent: {
    html: `<tag-button text='menu' segmented>
  <tag-menu-item name='approve' icon='Tick' text='Approve' description='Use this option to approve'></tag-menu-item>
  <tag-menu-item name='cancel' icon='Cross' text='Cancel' description='Use this option to cancel'></tag-menu-item>
</tag-button>`,
  },
  coreComponent_v3: {
    html: `<tag3-button text='menu' segmented>
  <tag3-menu-item name='approve' icon='Tick' text='Approve' description='Use this option to approve'></tag3-menu-item>
  <tag3-menu-item name='cancel' icon='Cross' text='Cancel' description='Use this option to cancel'></tag3-menu-item>
</tag3-button>`,
  },
  reactWrapper: {
    jsx: `<TagButton
  text='menu'
  segmented
  menuItems={[{
    name: 'approve',
    icon: 'Tick',
    text: 'Approve',
    description: 'Use this option to approve'
  }, {
    name: 'cancel',
    icon: 'Cross',
    text: 'Cancel',
    description: 'Use this option to cancel'
  }]}
/>`,
  },
  reactWrapper_v3: {
    jsx: `<Tag3Button id="1"
  text='menu'
  segmented
  menuItems={[{
    name: 'approve',
    icon: 'Tick',
    text: 'Approve',
    description: 'Use this option to approve'
  }, {
    name: 'cancel',
    icon: 'Cross',
    text: 'Cancel',
    description: 'Use this option to cancel'
  }]}
/>`,
  },
};

const customStylesStory: Story = {
  title: 'Custom styles',
  description: 'You can style various parts of the button by using the `*-style` attributes',
  coreComponent: {
    html: `<tag-button
  {{text}}
  {{icon}}
  {{buttonStyle}}
  {{buttonTextStyle}}
  {{buttonTextContainerStyle}}
  {{buttonIconStyle}}
  {{buttonIconContainerStyle}} >
</tag-button>`,
    knobs: {
      text: { default: 'A button with custom styles', editor: 'textbox' },
      icon: {
        default: 'Bug',
        editor: 'dropdown',
        editorOptions: [''].concat(Icons),
      },
      buttonStyle: {
        default: '{"height": "50px"}',
        editor: 'multilinetextbox',
      },
      buttonTextStyle: {
        default: '{"font-size": "18px"}',
        editor: 'multilinetextbox',
      },
      buttonTextContainerStyle: {
        default: '{"padding": "0px 20px"}',
        editor: 'multilinetextbox',
      },
      buttonIconStyle: {
        default: '{"height": "40px", "width": "40px"}',
        editor: 'multilinetextbox',
      },
      buttonIconContainerStyle: {
        default: '{"margin": "0px 10px"}',
        editor: 'multilinetextbox',
      },
    },
  },
  coreComponent_v3: {
    html: `<tag3-button{{...knobs}}></tag3-button>`,
    knobs: {
      text: { default: 'A button with custom styles', editor: 'textbox' },
      icon: {
        default: 'Bug',
        editor: 'dropdown',
        editorOptions: [''].concat(Icons),
      },
      buttonStyle: {
        default: '{"height": "50px"}',
        editor: 'multilinetextbox',
      },
      buttonTextStyle: {
        default: '{"font-size": "18px"}',
        editor: 'multilinetextbox',
      },
      buttonTextContainerStyle: {
        default: '{"padding": "0px 20px"}',
        editor: 'multilinetextbox',
      },
      buttonIconStyle: {
        default: '{"height": "40px", "width": "40px"}',
        editor: 'multilinetextbox',
      },
      buttonIconContainerStyle: {
        default: '{"margin": "0px 10px"}',
        editor: 'multilinetextbox',
      },
    },
  },
  reactWrapper: {
    jsx: `<TagButton
  {{text}}
  {{icon}}
  {{buttonStyle}}
  {{buttonTextStyle}}
  {{buttonTextContainerStyle}}
  {{buttonIconStyle}}
  {{buttonIconContainerStyle}}
/>`,
    knobs: {
      text: { default: 'A button with custom styles', editor: 'textbox' },
      icon: {
        default: 'Bug',
        editor: 'dropdown',
        editorOptions: [''].concat(Icons),
      },
      buttonStyle: {
        default: '{height: "50px"}',
        editor: 'multilinetextbox',
        qualifierStart: '{',
        qualifierEnd: '}',
      },
      buttonTextStyle: {
        default: '{fontSize: "18px"}',
        editor: 'multilinetextbox',
        qualifierStart: '{',
        qualifierEnd: '}',
      },
      buttonTextContainerStyle: {
        default: '{padding: "0px 20px"}',
        editor: 'multilinetextbox',
        qualifierStart: '{',
        qualifierEnd: '}',
      },
      buttonIconStyle: {
        default: '{height: "40px", width: "40px"}',
        editor: 'multilinetextbox',
        qualifierStart: '{',
        qualifierEnd: '}',
      },
      buttonIconContainerStyle: {
        default: '{margin: "0px 10px"}',
        editor: 'multilinetextbox',
        qualifierStart: '{',
        qualifierEnd: '}',
      },
    },
  },
  reactWrapper_v3: {
    jsx: `
    <Tag3Button id="1"
    {{text}}
    {{icon}}
    {{buttonStyle}}
    {{buttonTextStyle}}
    {{buttonTextContainerStyle}}
    {{buttonIconStyle}}
    {{buttonIconContainerStyle}}
    />`,
    knobs: {
      text: { default: 'A button with custom styles', editor: 'textbox' },
      icon: {
        default: 'Bug',
        editor: 'dropdown',
        editorOptions: [''].concat(Icons),
      },
      buttonStyle: {
        default: '{height: "50px"}',
        editor: 'multilinetextbox',
        qualifierStart: '{',
        qualifierEnd: '}',
      },
      buttonTextStyle: {
        default: '{fontSize: "18px"}',
        editor: 'multilinetextbox',
        qualifierStart: '{',
        qualifierEnd: '}',
      },
      buttonTextContainerStyle: {
        default: '{padding: "0px 20px"}',
        editor: 'multilinetextbox',
        qualifierStart: '{',
        qualifierEnd: '}',
      },
      buttonIconStyle: {
        default: '{height: "40px", width: "40px"}',
        editor: 'multilinetextbox',
        qualifierStart: '{',
        qualifierEnd: '}',
      },
      buttonIconContainerStyle: {
        default: '{margin: "0px 10px"}',
        editor: 'multilinetextbox',
        qualifierStart: '{',
        qualifierEnd: '}',
      },
    },
  },
};

export const TagButtonDocs: Component = {
  type: 'component',
  name: 'button',
  description: 'Renders a button',
  mobileFriendly: false,
  preRelease: '',
  popular: true,
  props: [
    {
      name: 'accent',
      description: 'The colour of the button',
      type: 'string',
      default: 'btndefault',
    },
    {
      name: 'size',
      description: 'The vertical size of the button',
      type: 'default | large | small',
      default: 'default',
    },
    {
      name: 'type',
      description: 'The type of the button',
      type: 'string',
      default: 'button',
    },
    {
      name: 'icon',
      description: 'The icon name',
      type: 'string',
    },
    {
      name: 'iconAccent',
      description: 'The icon colour',
      type: 'string',
    },
    {
      name: 'badge',
      description: 'The badge value to go on the button',
      type: 'string',
    },
    {
      name: 'badgeAccent',
      description: 'The badge colour',
      type: 'string',
      default: 'primary',
    },
    {
      name: 'badgeInline',
      description:
        'If set to true the badge is positioned next to button text, otherwise the badge is displayed top right',
      type: 'boolean',
      default: 'false',
    },
    {
      name: 'rounded',
      description: 'Whether the button is rounded',
      type: 'boolean',
    },
    {
      name: 'squared',
      description: 'Whether the button is squared',
      type: 'boolean',
    },
    {
      name: 'disabled',
      description: 'Whether the button is disabled',
      type: 'boolean',
    },
    {
      name: 'minWidth',
      description: 'The button minimum width',
      type: 'string',
    },
    {
      name: 'text',
      description: 'The button text',
      type: 'string',
    },
    {
      name: 'buttonStyle',
      description: 'JSX style object in JSON to apply to the button',
      type: 'string',
    },
    {
      name: 'buttonTextStyle',
      description: 'JSX style object in JSON to apply to the text',
      type: 'string',
    },
    {
      name: 'buttonTextContainerStyle',
      description: 'JSX style object in JSON to apply to the text container',
      type: 'string',
    },
    {
      name: 'buttonIconStyle',
      description: 'JSX style object in JSON to apply to the icon',
      type: 'string',
    },
    {
      name: 'buttonIconContainerStyle',
      description: 'JSX style object in JSON to apply to the icon container',
      type: 'string',
    },
    {
      name: 'segmented',
      description: 'Whether the button has a menu segmented from the main button action',
      type: 'boolean',
    },
    {
      name: 'hideMenuIndicator',
      description: 'Whether to display the menu arrow indicator when a menu is available',
      type: 'boolean',
      default: 'true',
    },
    {
      name: 'menuPopupPosition',
      description: 'The popup position of the button menu',
      type:
        'auto | bottom | bottom-start | bottom-end | top | top-start | top-end | left | left-start | left-end | right | right-start | right-end',
    },
    {
      name: 'menuItemContainerStyle',
      description: 'JSX style object in JSON to apply to the menu item container',
      type: 'string',
    },
    {
      name: 'menuItemStyle',
      description: 'JSX style object in JSON to apply to the menu item',
      type: 'string',
    },
    {
      name: 'actionValue',
      description: 'V1 Only: A value assigned to the button',
      type: 'string',
    },
    {
      name: 'actionName',
      description: 'V1 Only: A action assigned to the button',
      type: 'string',
    },
    {
      name: 'buttonClickInclude',
      description:
        'You can add an id to the button which will be send in the buttonClick event. Data to include in the buttonClick event. Useful for buttons in templated lists when you want to get access to the list item data in the button click handler',
      type: 'string',
    },
    {
      name: 'colour-scheme',
      description:
        'V3 Only: Dark mode (theme) control. Default for normal browser driven control. Dark | Light to force respective styling regardless of browser setting.',
      type: 'dark | light | default',
      default: 'light',
    },
    {
      name: 'device',
      description: 'V3 Only: Set preferred styling theme for device type.',
      type: 'desktop | mobile',
      default: 'desktop',
    },
  ],
  reactPropOverrides: [
    {
      name: 'menuItems',
      description: 'The definition of the items for the button menu',
      type:
        '{name: string; icon?: Icon, iconAccent?: Accent, text: string, description?: string;, menuItemStyle?: CSSProperties; menuItemContainerStyle?: CSSProperties; disabled?: boolean;}[]',
    },
  ],
  methods: [],
  events: [
    {
      name: 'buttonClick',
      description: 'Triggers when the button is clicked',
      parameters: [],
    },
    {
      name: 'menuItemClick',
      description: 'Triggers when a menu item clicked',
      parameters: [
        {
          name: 'name',
          description: 'The name attribute of the menu item',
          type: 'string',
        },
        {
          name: 'text',
          description: 'The text attribute of the menu item',
          type: 'string',
        },
      ],
    },
  ],
  stories: [playgroundStory, menuStory, subMenuStory, segmentedMenuStory, customStylesStory],
};
